import { derivePrimitiveCameraFocus } from '../cameraFocus';
import { buildFlightWheel } from '../wheels';
import { projectDecidedNaturalComparison, projectEmbeddedNaturalComparison } from './embeddedComparison';
import { momentKey, momentPart, stepMoment } from '../primitiveMoment';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveOperationDirectionEffect } from '../../../_contract/_~generated/operationDirectionEffect';
import type { Config, PrimitiveWheelState } from '../../../_contract/config';
import type { PrimitiveKeyframe, PrimitiveStepState } from '../../../_contract/steps';
import type { NaturalMultiplicationTrace } from '../../../_contract/trace';

export function withMultiplicationComparisons(args: {
    config: Config;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    steps: readonly PrimitiveStepState[];
    trace: NaturalMultiplicationTrace;
    /** Comparisons after this index flip Decision in place; omitted keeps every comparison full. */
    fullComparisons?: number;
}): readonly PrimitiveStepState[] {
    const { config, trace, steps } = args;
    const result: PrimitiveStepState[] = [];
    let decision: PrimitiveWheelState | undefined;
    let channel: PrimitiveKeyframe['booleanRod'];
    let unit: PrimitiveWheelState | undefined;
    const isUnit = (wheel: PrimitiveWheelState) =>
        wheel.role === 'remaining-repetitions' && wheel.variant === 'flight';
    const dim = (wheels: readonly PrimitiveWheelState[]) => wheels.map(wheel =>
        wheel.role === 'multiplicand' && wheel.variant === undefined ? { ...wheel,
            bandOpacityByPower: Object.fromEntries(wheel.powers.map(power => [power, config.natural.display.band.spentOpacity])),
        } : wheel);
    const decorate = (wheels: readonly PrimitiveWheelState[]) => [
        ...wheels, ...(decision ? [decision] : []),
    ];
    const introduce = (cycle: number, resting: readonly PrimitiveWheelState[]) => {
        const introduction = steps.find(step => step.moment?.frames[0]?.kind === 'contribute'
            && step.moment.frames[0].ordinal?.index === cycle - 1
            && step.keyframes.some(frame => frame.events.some(({ event }) => event.eventKind === 'unit-introduced')));
        const template = introduction?.keyframes.at(-1)?.wheels.find(isUnit);
        const counter = resting.find(wheel => wheel.role === 'remaining-repetitions' && !wheel.variant);
        if (!counter) throw new Error('Multiplication Unit requires a resident Counter.');
        if (!template && trace.cycles.length) throw new Error('Multiplication cycle is missing its decrement Unit.');
        unit = template ?? {
            ...buildFlightWheel({ role: 'remaining-repetitions', value: 1, power: 0,
                position: [counter.position[0], counter.position[1],
                    counter.position[2] - config.base.display.wheel.standoffNearOffset],
                rotation: 0, countedDigits: [1], overhangDigits: [],
                receiverLocation: { receiverRole: 'remaining-repetitions', locationKind: 'standoff-near' } }),
            helperWheel: { key: 'unit:remaining-repetitions', kind: 'Unit' },
        };
        const wheels = decorate(resting);
        result.push({
            ...momentKey(stepMoment('multiplication-unit', { action: 'introduce' }, cycle)), label: 'Introduce Decrement Unit',
            description: 'The same Unit asks for permission and later records one contribution by decrementing Counter.',
            cameraFocus: { stationKey: 'overview', viewKey: 'overview',
                roles: ['multiplier', 'remaining-repetitions', 'multiplicand', 'product', 'less-than-decision'] },
            keyframes: [
                { wheels: [...wheels, { ...unit, wheelOpacity: 0 }], ...(channel ? { booleanRod: channel } : {}),
                    transition: 'instant', events: [] },
                { wheels: [...wheels, unit], ...(channel ? { booleanRod: channel } : {}),
                    transition: { durationMs: config.base.animation.fadeDurationMs ?? 0 },
                    events: introduction?.keyframes.flatMap(frame =>
                        frame.events.filter(({ event }) => event.eventKind === 'unit-introduced')) ?? [] },
            ],
        });
    };
    const compare = (index: number, residual: number, resting: readonly PrimitiveWheelState[], opening: boolean) => {
        if (!unit) throw new Error('Multiplication comparison requires its pending decrement Unit.');
        const options = {
            config, directionCatalog: args.directionCatalog,
            source: opening ? residual : 1, target: opening ? 1 : residual,
            sourceRole: 'remaining-repetitions' as const, targetRole: 'remaining-repetitions' as const,
            ...(opening ? { targetVariant: 'flight' as const } : { sourceVariant: 'flight' as const }),
            resting: [...dim(resting), unit], frame: momentPart('multiplication-comparison', undefined, index),
            view: opening ? 'multiplication-comparison-initial' : 'multiplication-comparison',
        };
        const comparison = args.fullComparisons === undefined || index <= args.fullComparisons
            ? projectEmbeddedNaturalComparison(options)
            : [projectDecidedNaturalComparison({ ...options, previous: decision, cameraFocus: { stationKey: 'overview',
                viewKey: 'overview', roles: ['multiplier', 'remaining-repetitions', 'multiplicand', 'product', 'less-than-decision'] } })];
        result.push(...comparison);
        const returned = comparison.at(-1)!.keyframes.at(-1)!;
        decision = returned.wheels.find(wheel => wheel.role === 'less-than-decision')!;
        channel = returned.booleanRod;
    };
    const gate = (index: number, revive: boolean, resting: readonly PrimitiveWheelState[]) => {
        const wheels = dim(resting);
        result.push({
            ...momentKey({ frames: [momentPart('multiplication-comparison', undefined, index)], step: momentPart('gate') }),
            label: revive ? 'Enable Multiplicand' : 'Finish Multiplication',
            description: revive ? 'The comparison grants permission; revive only after returning and any pending decrement settles.'
                : 'No further contribution is permitted. Keep Multiplicand dimmed.',
            cameraFocus: { stationKey: 'overview', viewKey: 'overview',
                roles: ['multiplier', 'remaining-repetitions', 'multiplicand', 'product', 'less-than-decision'] },
            keyframes: [
                { wheels: decorate(wheels), booleanRod: channel, transition: 'instant', events: [] },
                { wheels: decorate(revive ? wheels.map(wheel => wheel.role === 'multiplicand' && !wheel.variant
                    ? { ...wheel, bandOpacityByPower: undefined } : wheel) : wheels),
                    booleanRod: channel, transition: { durationMs: config.base.animation.fadeDurationMs ?? 0 }, events: [] },
            ],
        });
    };
    let previous: readonly PrimitiveWheelState[] = [];
    for (const [at, step] of steps.entries()) {
        const setup = ['initial', 'rod-extend', 'remaining-repetitions-copy'].includes(step.stepKey);
        const cleanup = ['remaining-repetitions-remove', 'rod-contract'].includes(step.stepKey);
        const momentFrame = step.moment?.frames[0];
        const contribution = momentFrame?.kind === 'contribute';
        const focus = derivePrimitiveCameraFocus(step, previous);
        const mapped: PrimitiveStepState = { ...step, cameraFocus: focus,
            keyframes: step.keyframes.map(frame => {
                let wheels = contribution && unit ? [...frame.wheels.filter(wheel => !isUnit(wheel)), unit] : frame.wheels;
                if (setup || step.moment?.step.kind === 'decrement' || momentFrame?.kind === 'composite') wheels = dim(wheels);
                const opacity = frame.wheels.find(wheel => wheel.role === 'remaining-repetitions' && !wheel.variant)?.wheelOpacity ?? 1;
                const hasCounter = frame.wheels.some(wheel => wheel.role === 'remaining-repetitions');
                return { ...frame, wheels: cleanup ? [
                    ...wheels, ...(decision && hasCounter ? [{ ...decision, wheelOpacity: opacity }] : []),
                ] : decorate(wheels),
                events: frame.events.filter(({ event }) => event.eventKind !== 'unit-introduced'),
                ...(channel ? { booleanRod: cleanup ? { segments: hasCounter
                    ? channel.segments.map(segment => ({ ...segment, opacity: segment.opacity * opacity })) : [] } : channel } : {}) };
            }),
        };
        result.push(mapped);
        previous = step.keyframes.at(-1)!.wheels;
        const resting = mapped.keyframes.at(-1)!.wheels.filter(wheel => wheel.role !== 'less-than-decision' && !isUnit(wheel));
        if (step.stepKey === 'remaining-repetitions-copy') {
            introduce(1, resting);
            compare(1, trace.multiplier, resting, true);
            gate(1, trace.multiplier > 0, [...resting, unit!]);
            if (!trace.cycles.length) {
                const final = result.at(-1)!.keyframes.at(-1)!;
                result.push({
                    ...momentKey(stepMoment('multiplication-unit', { action: 'retire' })), label: 'Retire Unused Unit',
                    description: 'Counter is zero. Remove the unused Unit without attempting a subtractive settlement.',
                    cameraFocus: result.at(-1)!.cameraFocus,
                    keyframes: [
                        { ...final, transition: 'instant' },
                        { ...final, wheels: final.wheels.map(wheel => isUnit(wheel) ? { ...wheel, wheelOpacity: 0 } : wheel),
                            transition: { durationMs: config.base.animation.fadeDurationMs ?? 0 }, events: [] },
                        { ...final, wheels: final.wheels.filter(wheel => !isUnit(wheel)), transition: 'instant', events: [] },
                    ],
                });
                unit = undefined;
            }
        }
        const cycle = contribution ? trace.cycles.find(cycle => momentFrame.ordinal?.index === cycle.cycleIndex - 1) : undefined;
        const nextFrame = steps[at + 1]?.moment?.frames[0];
        if (cycle && (nextFrame?.kind !== 'contribute' || nextFrame.ordinal?.index !== cycle.cycleIndex - 1)) {
            compare(cycle.cycleIndex + 1, cycle.residualBefore, resting, false);
        }
        if (step.keyframes.some(frame => frame.events.some(({ event }) => event.eventKind === 'unit-decrement-completed'))) {
            const cycle = step.moment?.step.kind === 'decrement'
                ? trace.cycles.find(cycle => step.moment?.step.ordinal?.index === cycle.cycleIndex - 1) : undefined;
            if (!cycle) throw new Error('Counter settlement has no multiplication cycle.');
            unit = undefined;
            gate(cycle.cycleIndex + 1, cycle.residualBefore > 1, resting);
            if (cycle.residualAfter > 0) introduce(cycle.cycleIndex + 1,
                result.at(-1)!.keyframes.at(-1)!.wheels.filter(wheel => wheel.role !== 'less-than-decision'));
        }
    }
    return result;
}
